import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import {
  fetchHolterEventsDailyCount,
  generateFilterSpecialEventType,
  isAppliedFilterEvent,
} from '@/entities/event';
import {
  KEY_CANCEL,
  KEY_RECOIL,
  TYPE_BEAT_EVENT_ENUM,
  TYPE_RHYTHM_EVENT_ENUM,
  canAvgRhythmEvents,
  canShowCountBeatEvents,
  canShowDurationEvents,
  LIST_RUN_EVENT,
} from '@/shared/config';
import {
  // eventTypeConvert,
  convertToMomentJs,
} from '@/shared/lib/aiUtils';
import { useUpdateEffect } from '@/shared/lib/customHooks';

import { logError } from '@/shared/lib/apiHelpers';
import {
  selectActiveTab,
  selectDailyCount,
  selectEventFilter,
  selectIsEmptyData,
  selectIsLoadingHrChart,
  selectIsLoadingHrChartOptions,
  selectJumpDate,
  selectSelectedDateValue,
  selectHolterEvents,
  selectSelectedStrip,
  useReportStore,
} from '@/entities/report';
import type { HrChartOptionsProps } from './index';

/**
 * Drives the date navigation strip above the heart-rate chart on the rhythm events tab. Fetches per-day event counts for the active event type with the special-event ranges and event filter applied, derives the MM/DD dropdown labels, and keeps the selected day in the report store in sync with the selected strip, jump-to-date requests and the daily-count list, clearing pending jump state on every manual change.
 *
 * @param props - Study, profile, report range and timezone offset the daily counts are fetched and formatted against.
 * @returns An object carrying the two loading flags, dateIndex, dailyCount, dropdownOptions, formatSelectedDate, onChangeDate, handleClickArrow, isEmptyData and isAfibEvent.
 */
const useHrChartOptions = ({
  timezoneOffset,
  studyId,
  profileId,
  startReport,
  stopReport,
}: HrChartOptionsProps) => {
  const keyRecoil = KEY_RECOIL.RHYTHM_EVENT;

  const isLoadingHrChartOptions = useReportStore(selectIsLoadingHrChartOptions(keyRecoil));
  const setIsLoadingHrChartOptionsAction = useReportStore(
    (s) => s.actions.setIsLoadingHrChartOptions,
  );
  /**
   * Toggles the loading state of the heart-rate chart's date controls. Forwards the value to the store action under this tab's key.
   *
   * @param v - The new loading flag, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setIsLoadingHrChartOptions = (v: Parameters<typeof setIsLoadingHrChartOptionsAction>[1]) =>
    setIsLoadingHrChartOptionsAction(keyRecoil, v);
  const activeButton = useReportStore(selectActiveTab(keyRecoil));
  const dailyCount = useReportStore(selectDailyCount(keyRecoil));
  const setDailyCountAction = useReportStore((s) => s.actions.setDailyCount);
  /**
   * Writes the per-day event counts for the rhythm events tab. Forwards the value to the store action under this tab's key.
   *
   * @param v - The new daily-count list, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setDailyCount = (v: Parameters<typeof setDailyCountAction>[1]) =>
    setDailyCountAction(keyRecoil, v);
  /**
   * Reads the day currently selected for the heart-rate chart without subscribing to it. Runs the selector against the report store's snapshot so handlers see the value committed so far rather than the one captured at render.
   *
   * @returns The selected day as an ISO date string.
   */
  const getSelectedDateValue = () => selectSelectedDateValue(keyRecoil)(useReportStore.getState());
  const setSelectedDateValueAction = useReportStore((s) => s.actions.setSelectedDateValue);
  /**
   * Records the day currently shown by the heart-rate chart. Forwards the value to the store action under this tab's key.
   *
   * @param v - The new selected date as an ISO string, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setSelectedDateValue = (v: Parameters<typeof setSelectedDateValueAction>[1]) =>
    setSelectedDateValueAction(keyRecoil, v);
  const setIsShowAfibEventAction = useReportStore((s) => s.actions.setIsShowAfibEvent);
  /**
   * Toggles display of AFib events on the heart-rate chart. Forwards the value to the store action under this tab's key.
   *
   * @param v - The new AFib visibility flag, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setIsShowAfibEvent = (v: Parameters<typeof setIsShowAfibEventAction>[1]) =>
    setIsShowAfibEventAction(keyRecoil, v);
  const setIsShowOtherEventAction = useReportStore((s) => s.actions.setIsShowOtherEvent);
  /**
   * Toggles display of the non-AFib events on the heart-rate chart. Forwards the value to the store action under this tab's key.
   *
   * @param v - The new other-event visibility flag, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setIsShowOtherEvent = (v: Parameters<typeof setIsShowOtherEventAction>[1]) =>
    setIsShowOtherEventAction(keyRecoil, v);
  const isLoadingHrChart = useReportStore(selectIsLoadingHrChart(keyRecoil));
  const selectedStrip = useReportStore(selectSelectedStrip(keyRecoil));
  const jumpDate = useReportStore(selectJumpDate(keyRecoil));
  const setJumpDateAction = useReportStore((s) => s.actions.setJumpDate);
  /**
   * Records the date the heart-rate chart should jump to. Forwards the value to the store action under this tab's key.
   *
   * @param v - The new jump date, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setJumpDate = (v: Parameters<typeof setJumpDateAction>[1]) =>
    setJumpDateAction(keyRecoil, v);
  const setJumpTimeAction = useReportStore((s) => s.actions.setJumpTime);
  /**
   * Records the time within the jump target date the chart should scroll to. Forwards the value to the store action under this tab's key.
   *
   * @param v - The new jump time, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setJumpTime = (v: Parameters<typeof setJumpTimeAction>[1]) =>
    setJumpTimeAction(keyRecoil, v);
  const setJumpPortionAction = useReportStore((s) => s.actions.setJumpPortion);
  /**
   * Records which portion of the jump target day the chart should scroll to. Forwards the value to the store action under this tab's key.
   *
   * @param v - The new jump portion, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setJumpPortion = (v: Parameters<typeof setJumpPortionAction>[1]) =>
    setJumpPortionAction(keyRecoil, v);
  const isEmptyData = useReportStore(selectIsEmptyData(keyRecoil));
  const setIsEmptyDataAction = useReportStore((s) => s.actions.setIsEmptyData);
  /**
   * Flags whether the selected day holds no ECG data on the rhythm events tab. Forwards the value to the store action under this tab's key.
   *
   * @param v - The new empty-data flag, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setIsEmptyData = (v: Parameters<typeof setIsEmptyDataAction>[1]) =>
    setIsEmptyDataAction(keyRecoil, v);
  const dateList = useReportStore((s) => s.dateList);
  const setDateList = useReportStore((s) => s.actions.setDateList);
  const holterEvents = useReportStore(selectHolterEvents(keyRecoil));
  const specialEventTypes = useReportStore((s) => s.specialEventTypes);
  const eventFilter = useReportStore(selectEventFilter(keyRecoil));
  const selectedDateValue = useReportStore(selectSelectedDateValue(keyRecoil));

  const [dateIndex, setDateIndex] = useState(0);

  const isAfibEvent = activeButton === TYPE_RHYTHM_EVENT_ENUM.AFIB;

  const dropdownOptions = useMemo(
    () =>
      _.map(
        dailyCount,
        (x) =>
          `${convertToMomentJs(x.date, timezoneOffset).format('MM/DD')} ${x.count === -1 ? '(No data)' : `(${x.count})`}`,
      ),
    [dailyCount],
  );

  /**
   * Loads the per-day holter event counts backing the date dropdown. Builds the query from the active event type plus its special-event ranges, layering on the reviewed, captured, average-HR and sort criteria when an event filter is applied, awaits the daily-count API under the holter cancel key, stores the result and logs any failure while always clearing the loading flag.
   *
   * @returns A promise resolving once the daily counts are stored and the loading flag is cleared.
   */
  const handleFetchHolterEventsDailyCount = async () => {
    setIsLoadingHrChartOptions(true);
    try {
      // activeTabState also allows TBeatHrActiveTab; narrowed here to match the LIST_RUN_EVENT/canShow*/canAvg* arrays' element type.
      const type = activeButton as TYPE_RHYTHM_EVENT_ENUM | TYPE_BEAT_EVENT_ENUM;
      const additionalFilter = generateFilterSpecialEventType(type, specialEventTypes);
      if (isAppliedFilterEvent(eventFilter, type)) {
        _.assign(additionalFilter, {
          ...(eventFilter.isHideReviewed && { isReviewed: false }),
          ...(eventFilter.isCapture && { isCaptured: true }),
          ...(eventFilter.isAvgHrBelow100 &&
            LIST_RUN_EVENT.includes(type) && { isAvgHrBelow100: true }),
          ...(eventFilter.sortOrder &&
            (canShowCountBeatEvents.includes(type) ||
              canShowDurationEvents.includes(type as TYPE_RHYTHM_EVENT_ENUM) ||
              canAvgRhythmEvents.includes(type as TYPE_RHYTHM_EVENT_ENUM)) && {
              sortBy: eventFilter.sortBy || 'countBeats',
              sortOrder: eventFilter.sortOrder,
            }),
        });
      }
      const holterEventsDailyCountFilter = {
        studyId,
        profileId,
        types: [type],
        ...additionalFilter,
        startSearchDate: startReport,
        stopSearchDate: stopReport,
      };
      const dailyCount = await fetchHolterEventsDailyCount(
        holterEventsDailyCountFilter,
        KEY_CANCEL.API_HOLTER_AI,
      );
      setDailyCount(dailyCount);
    } catch (error) {
      logError('Failed to fetch holter events daily count: ', error);
    }
    setIsLoadingHrChartOptions(false);
  };

  /**
   * Applies a day picked from the dropdown to the heart-rate chart. Resolves the label back to its daily-count row and, only when the resulting ISO date differs from the one already in the store, clears the jump date, time and portion, moves dateIndex and flags the day as empty when its count is -1.
   *
   * @param name - Name of the dropdown field that changed.
   * @param value - The selected dropdown label, matched against the generated MM/DD options.
   * @returns Nothing.
   */
  const onChangeDate = (name: string, value: string) => {
    const index = _.findIndex(dropdownOptions, (x) => x === value);
    if (index > -1) {
      const selectedDateMoment = convertToMomentJs(dailyCount[index].date, timezoneOffset);
      const changedSelectedDateValue = selectedDateMoment.toISOString();
      if (changedSelectedDateValue !== getSelectedDateValue()) {
        setJumpDate(undefined);
        setJumpTime(undefined);
        setJumpPortion(0);
        setDateIndex(index);
        setSelectedDateValue(changedSelectedDateValue);
        setIsEmptyData(dailyCount[index].count === -1);
      }
    }
  };

  /**
   * Steps the heart-rate chart one day left or right. Clears any pending jump date, time and portion, moves dateIndex, publishes the neighbouring day's date to the store and flags the day as empty when its count is -1.
   *
   * @param isRight - True to advance to the next day, false to step back one.
   * @returns Nothing.
   */
  const handleClickArrow = (isRight: boolean) => {
    const newDateIndex = isRight ? dateIndex + 1 : dateIndex - 1;
    setJumpDate(undefined);
    setJumpTime(undefined);
    setJumpPortion(0);
    setDateIndex(newDateIndex);
    setSelectedDateValue(dailyCount[newDateIndex].date);
    setIsEmptyData(dailyCount[newDateIndex].count === -1);
  };

  useEffect(() => {
    handleFetchHolterEventsDailyCount();
    if (isAfibEvent) {
      setIsShowAfibEvent(true);
    } else {
      setIsShowOtherEvent(true);
    }
  }, [activeButton, specialEventTypes, eventFilter]);

  useUpdateEffect(() => {
    if (!isLoadingHrChartOptions) {
      if (!_.isEmpty(selectedStrip)) {
        const rhythmEventStripStartDate = convertToMomentJs(selectedStrip.start, timezoneOffset);
        const rhythmEventStripStopDate = convertToMomentJs(selectedStrip.stop, timezoneOffset);
        const rhythmEventStripDateString = rhythmEventStripStartDate.format('MM/DD');
        let index = _.findIndex(dateList, (x) => x.format('MM/DD') === rhythmEventStripDateString);

        let shouldPreventReselectCurrentDay = false;
        let currentIndex = index;
        const currentDateIsoString = getSelectedDateValue();
        if (currentDateIsoString) {
          currentIndex = _.findIndex(dateList, (x) => x.toISOString() === currentDateIsoString);
          const currentDate = convertToMomentJs(currentDateIsoString, timezoneOffset);
          const isEventBetweenDays = currentDate.isBetween(
            rhythmEventStripStartDate,
            rhythmEventStripStopDate,
            undefined,
            '[]',
          );
          shouldPreventReselectCurrentDay = currentIndex !== index && isEventBetweenDays;
        }

        if (index > -1) {
          if (shouldPreventReselectCurrentDay) {
            index = currentIndex;
          }
          setDateIndex(index);
          setSelectedDateValue(dateList[index].toISOString());
          setIsEmptyData(dailyCount[index].count === -1);
        }
      } else if (holterEvents.length === 0 && dateList.length) {
        setDateIndex(0);
        setSelectedDateValue(dateList[0].toISOString());
        setIsEmptyData(dailyCount[0].count === -1);
      }
    }
  }, [isLoadingHrChartOptions, selectedStrip?.id, dateList, holterEvents]);

  useUpdateEffect(() => {
    if (jumpDate) {
      const rhythmEventStripDateString = convertToMomentJs(jumpDate, timezoneOffset).format(
        'MM/DD',
      );
      const index = _.findIndex(dateList, (x) => x.format('MM/DD') === rhythmEventStripDateString);
      if (index > -1) {
        setDateIndex(index);
        setSelectedDateValue(dateList[index].toISOString());
      }
    }
  }, [jumpDate]);

  useEffect(() => {
    const dateListTmp = _.map(dailyCount, (x) => convertToMomentJs(x.date, timezoneOffset));
    setDateList(dateListTmp);
  }, [dailyCount]);

  useEffect(() => {
    if (selectedDateValue) {
      const findIndex = _.findIndex(dailyCount, (x) => x.date === selectedDateValue);
      setIsEmptyData(dailyCount[findIndex]?.count === -1);
    }
  }, [selectedDateValue, dailyCount]);
  const formatSelectedDate = dropdownOptions[dateIndex];

  return {
    isLoadingHrChartOptions,
    isLoadingHrChart,
    dateIndex,
    dailyCount,
    dropdownOptions,
    formatSelectedDate,
    onChangeDate,
    handleClickArrow,
    isEmptyData,
    isAfibEvent,
  };
};

export default useHrChartOptions;
